import { useEffect, useState } from "react";
import axios from "axios";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { API, captureAttribution, scrollToId } from "@/lib/contact";
import { Seo, SeoJsonLd } from "@/components/Seo";
import { AgencyNav, AgencyFooter, AgencyFloating } from "@/agency/AgencyLayout";
import { AgencyHero } from "@/agency/AgencyHero";
import { AgencyServices } from "@/agency/AgencyServices";
import { AgencyIndustries } from "@/agency/AgencyIndustries";
import { AgencyPackages } from "@/agency/AgencyPackages";
import { AgencyPortfolio } from "@/agency/AgencyPortfolio";
import { AgencyAuditForm } from "@/agency/AgencyAudit";
import { AgencyConsultation } from "@/agency/AgencyConsultation";
import { AgencyFAQ } from "@/agency/AgencyFAQ";

export default function AgencyPage() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    captureAttribution();
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    axios.get(`${API}/agency/content`).then((r) => setContent(r.data)).catch(() => setContent({}));
    return () => { cancelAnimationFrame(raf); lenis.destroy(); window.__lenis = null; };
  }, []);

  const faqLd = content?.faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: content.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <div className="bg-background min-h-screen font-body overflow-x-clip">
      <Seo
        title="LaunchX Agency — Digital Marketing for Local Businesses | Rampur"
        description="Websites, Google Business Profile, local SEO, Meta and Google Ads for local businesses in Rampur and across India. Free digital audit and consultation — real enquiries, honest tracking."
        path="/agency"
      />
      <SeoJsonLd data={{
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        name: "LaunchX Agency",
        description: "Digital marketing agency for local businesses: websites, Google Business Profile, local SEO, Meta and Google Ads.",
        url: "https://launchxacademy.com/agency",
        telephone: "+91 97623 84018",
        areaServed: "India",
        address: { "@type": "PostalAddress", addressLocality: "Rampur", addressRegion: "Uttar Pradesh", addressCountry: "IN" },
      }} />
      {faqLd && <SeoJsonLd data={faqLd} />}
      <AgencyNav scrollTo={scrollToId} />
      <main>
        <AgencyHero scrollTo={scrollToId} />
        <AgencyServices />
        <AgencyIndustries />
        <AgencyPackages content={content} scrollTo={scrollToId} />
        <AgencyPortfolio content={content} scrollTo={scrollToId} />
        <AgencyAuditForm />
        <AgencyConsultation content={content} />
        <AgencyFAQ content={content} />
      </main>
      <AgencyFooter />
      <AgencyFloating />
      <Toaster position="top-center" richColors />
    </div>
  );
}
